// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Badge, Button, Group, Loader, Modal, Select, Stack, Table, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { getReferenceString } from '@medplum/core';
import type { AppointmentParticipant } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconCalendarRepeat, IconNotes } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { AppointmentDetails } from '../../hooks/useAppointmentDetails';
import { cancelProviderAppointment } from '../../services/appointment-lifecycle-service';
import { formatAppointmentDate, formatCopay, isCheckInAppointment } from '../../utils/appointment-formatting';
import { AppointmentTimeWithTimezone } from '../appointments/AppointmentTimeWithTimezone';
import { AppointmentCancellationModal } from '../schedule/AppointmentCancellationModal';
import { AppointmentDetailsModal } from '../schedule/AppointmentDetailsModal';
import { AppointmentJoinButton } from '../schedule/AppointmentJoinButton';

interface UpcomingAppointmentsTableProps {
  /** Enriched appointment details to display */
  appointments: AppointmentDetails[];
  /** Whether appointment data is still loading */
  loading: boolean;
  /** Patient ID — used to build reschedule route under /Patient/:patientId */
  patientId: string;
  /** Display name for the patient chart modal heading. */
  patientName?: string;
  /** Whether the current user can schedule/reschedule/cancel appointments */
  canSchedule: boolean;
  /** Whether the current user can conduct (join) appointments (PA/MD only) */
  canConduct: boolean;
  /** Callback to refresh the appointment list after cancellation */
  onRefresh?: () => void;
}

/** Preset cancellation reasons written to the FHIR Appointment via `cancelAppointment`. */
const CANCEL_REASON_OPTIONS = [
  { value: 'Patient request', label: 'Patient request' },
  { value: 'Provider unavailable', label: 'Provider unavailable' },
  { value: 'Duplicate booking', label: 'Duplicate booking' },
  { value: 'No longer needed', label: 'No longer needed' },
  { value: 'Other', label: 'Other' },
];

function getEncounterNoteTitle(appointment: AppointmentDetails['appointment'], canConduct: boolean): string {
  if (!canConduct) {
    return 'Your role cannot conduct appointments';
  }
  return isCheckInAppointment(appointment) ? 'Open coordinator note' : 'Open encounter note';
}

/**
 * Renders the provider-context payment-status text for an appointment cell.
 *
 * Providers see plain text — unlike the patient app, there is no "Pay Now" or
 * "View Receipt" link (clinicians don't collect payment inline).
 */
function PaymentStatusCell({ details }: { details: AppointmentDetails }): JSX.Element {
  if (details.paymentStatus === 'paid') {
    return <Text size="sm">Paid</Text>;
  }
  if (details.paymentStatus === 'pending') {
    return <Text size="sm">Pending</Text>;
  }
  return (
    <Text size="sm" c="dimmed">
      n/a
    </Text>
  );
}

/**
 * Table displaying a patient's upcoming (booked, future) appointments from the
 * clinician's point of view.
 *
 * Differences vs. the patient-app version:
 *   - Includes a Required Forms column (currently a placeholder dash).
 *   - Payment Status is plain text — no Pay Now / View Receipt links.
 *   - Action buttons are role-gated via `canSchedule` / `canConduct`.
 *   - Reschedule navigates to the patient-scoped `/Patient/:patientId/Appointment/reschedule/:id` route.
 * @param props - Component props
 * @returns The upcoming appointments table
 */
export function UpcomingAppointmentsTable({
  appointments,
  loading,
  patientId,
  patientName,
  canSchedule,
  canConduct,
  onRefresh,
}: UpcomingAppointmentsTableProps): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [cancelModalOpened, cancelModalHandlers] = useDisclosure(false);
  const [confirmOpen, confirmHandlers] = useDisclosure(false);
  const [cancelTarget, setCancelTarget] = useState<AppointmentDetails | undefined>();
  const [cancelReason, setCancelReason] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [joinTarget, setJoinTarget] = useState<AppointmentDetails | undefined>();

  function openCancelModal(details: AppointmentDetails): void {
    setCancelTarget(details);
    setCancelReason(null);
    cancelModalHandlers.open();
  }

  function closeCancelModal(): void {
    cancelModalHandlers.close();
    setCancelReason(null);
  }

  function gotoReschedule(appointmentId: string | undefined): void {
    if (!appointmentId) {
      return;
    }
    navigate(`/Patient/${patientId}/Appointment/reschedule/${appointmentId}`);
  }

  function closeJoinModal(): void {
    setJoinTarget(undefined);
  }

  async function handleOpenEncounterNote(details: AppointmentDetails): Promise<void> {
    if (!details.appointment.id) {
      return;
    }

    try {
      const encounters = await medplum.searchResources('Encounter', {
        appointment: getReferenceString(details.appointment),
        _count: '1',
      });
      const encounter = encounters[0];
      const patientReference = encounter?.subject?.reference;

      if (encounter?.id && patientReference) {
        navigate(`/${patientReference}/Encounter/${encounter.id}`)?.catch(console.error);
        return;
      }

      showNotification({
        color: 'yellow',
        title: 'Encounter note unavailable',
        message: 'No encounter has been created for this appointment yet.',
      });
    } catch (err) {
      showNotification({
        color: 'red',
        title: 'Unable to open encounter note',
        message: err instanceof Error ? err.message : 'Failed to find the encounter for this appointment.',
      });
    }
  }

  async function handleCancel(): Promise<void> {
    if (!cancelTarget?.appointment.id) {
      return;
    }

    setIsCancelling(true);
    try {
      await cancelProviderAppointment(medplum, cancelTarget.appointment.id, cancelReason);

      showNotification({
        color: 'green',
        title: 'Appointment cancelled',
        message: 'The appointment has been cancelled successfully.',
      });

      closeCancelModal();
      onRefresh?.();
    } catch (err) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to cancel appointment',
      });
    } finally {
      setIsCancelling(false);
    }
  }

  if (loading) {
    return (
      <Stack align="center" py="md">
        <Loader size="sm" />
      </Stack>
    );
  }

  if (appointments.length === 0) {
    return (
      <Text c="dimmed" py="md" ta="center">
        No upcoming appointments.
      </Text>
    );
  }

  return (
    <>
      <Table highlightOnHover withTableBorder miw={1000}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Type</Table.Th>
            <Table.Th>Date</Table.Th>
            <Table.Th>Time</Table.Th>
            <Table.Th>Amount</Table.Th>
            <Table.Th>Payment Status</Table.Th>
            <Table.Th>Required Forms</Table.Th>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {appointments.map((details) => (
            <Table.Tr key={details.appointment.id}>
              <Table.Td>
                <Group gap="xs">
                  <Text size="sm">{details.appointmentType}</Text>
                  {details.appointment.status === 'arrived' && (
                    <Badge size="xs" color="green" variant="light">
                      Arrived
                    </Badge>
                  )}
                  {details.appointment.status === 'checked-in' && (
                    <Badge size="xs" color="blue" variant="light">
                      Checked In
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{formatAppointmentDate(details.appointment.start)}</Text>
              </Table.Td>
              <Table.Td>
                <AppointmentTimeWithTimezone
                  start={details.appointment.start}
                  end={details.appointment.end}
                  textProps={{ size: 'sm' }}
                />
              </Table.Td>
              <Table.Td>
                <Text size="sm">{formatCopay(details.copay)}</Text>
              </Table.Td>
              <Table.Td>
                <PaymentStatusCell details={details} />
              </Table.Td>
              <Table.Td>
                {/* TODO(appointments-required-forms): render links to required
                    intake questionnaires once ActivityDefinition→Questionnaire
                    mapping is authored. Until then, show a dash placeholder. */}
                {details.requiredForms.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    —
                  </Text>
                ) : (
                  <Stack gap={2}>
                    {details.requiredForms.map((form) => (
                      <Text key={form.url} size="sm">
                        {form.title}
                      </Text>
                    ))}
                  </Stack>
                )}
              </Table.Td>
              <Table.Td>
                <Text size="sm">{details.providerName}</Text>
              </Table.Td>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <AppointmentJoinButton
                    joinUrl={details.joinUrl}
                    canConduct={canConduct}
                    onClick={() => setJoinTarget(details)}
                  />
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconNotes size={14} />}
                    onClick={() => handleOpenEncounterNote(details)}
                    disabled={!canConduct}
                    title={getEncounterNoteTitle(details.appointment, canConduct)}
                  >
                    {isCheckInAppointment(details.appointment) ? 'Open coordinator note' : 'Open encounter note'}
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconCalendarRepeat size={14} />}
                    onClick={() => gotoReschedule(details.appointment.id)}
                    disabled={!canSchedule}
                    title={!canSchedule ? 'Your role cannot schedule appointments' : undefined}
                  >
                    Reschedule
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => openCancelModal(details)}
                    disabled={!canSchedule}
                    title={!canSchedule ? 'Your role cannot cancel appointments' : undefined}
                  >
                    Cancel
                  </Button>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {joinTarget && (
        <AppointmentDetailsModal
          opened
          onClose={closeJoinModal}
          appointment={joinTarget}
          patientId={patientId}
          patientName={patientName}
        />
      )}

      <Modal
        opened={cancelModalOpened}
        onClose={closeCancelModal}
        overlayProps={{ opacity: confirmOpen ? 0 : 0.55 }}
        centered
        withCloseButton={false}
        padding="xl"
        radius="lg"
        size="lg"
      >
        {cancelTarget && (
          <Stack gap="lg" align="center">
            <Title order={2}>Cancel appointment</Title>

            <Stack gap={4} align="center">
              <Text fw={600} size="md">
                {cancelTarget.appointmentType}
              </Text>
              <Text fw={600} size="lg">
                {formatAppointmentDate(cancelTarget.appointment.start)}
              </Text>
              <AppointmentTimeWithTimezone
                start={cancelTarget.appointment.start}
                end={cancelTarget.appointment.end}
                groupProps={{ justify: 'center' }}
                textProps={{ fw: 600, size: 'lg' }}
              />
            </Stack>

            <Text size="sm" c="dimmed" ta="center">
              Are you sure you want to cancel this appointment? This will remove it from the patient's calendar. If you
              prefer to reschedule, choose Reschedule Instead.
            </Text>

            <Select
              label="Reason for cancellation"
              placeholder="Select a reason"
              data={CANCEL_REASON_OPTIONS}
              value={cancelReason}
              onChange={setCancelReason}
              w="100%"
              withAsterisk
              allowDeselect={false}
            />

            <Group justify="center" w="100%">
              <Button
                variant="outline"
                onClick={() => {
                  const id = cancelTarget.appointment.id;
                  closeCancelModal();
                  gotoReschedule(id);
                }}
              >
                Reschedule Instead
              </Button>
              <Button color="red" onClick={confirmHandlers.open} loading={isCancelling}>
                Yes, cancel
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      <AppointmentCancellationModal
        opened={confirmOpen}
        onClose={confirmHandlers.close}
        onConfirm={handleCancel}
        confirmLabel="Confirm Cancellation"
        loading={isCancelling}
      />
    </>
  );
}
